<script setup>
import { computed, onMounted, ref } from 'vue';

import TeamsAPI from 'dashboard/api/teams';
import TicketsAPI from 'dashboard/api/tickets';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const loading = ref(true);
const saving = ref(false);

const teams = ref([]);
const settings = ref([]);

const selectedTeamId = ref(null);
const draft = ref(null);

const selectedTeam = computed(() =>
  teams.value.find(team => Number(team.id) === Number(selectedTeamId.value))
);

const selectedSetting = computed(() =>
  settings.value.find(
    setting => Number(setting.team_id) === Number(selectedTeamId.value)
  )
);

const emptyMail = team => ({
  id: null,

  team_id: team?.id || null,

  transport_mode: 'alias',

  inbound_enabled: true,
  outbound_enabled: true,

  sender_name: team?.name || '',

  sender_email: '',

  inbound_domain: '',
  inbound_local_part: '',

  smtp_address: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_domain: '',
  smtp_authentication: 'login',

  smtp_enable_starttls_auto: true,
  smtp_ssl: false,

  has_smtp_password: false,
});

const normalizeTeams = response => {
  const data = response?.data?.payload ?? response?.data ?? [];

  return Array.isArray(data) ? data : [];
};

const load = async () => {
  loading.value = true;

  try {
    const [teamsResponse, settingsResponse] = await Promise.all([
      TeamsAPI.get(true),
      TicketsAPI.departmentMailSettings(),
    ]);

    teams.value = normalizeTeams(teamsResponse);

    settings.value =
      settingsResponse?.data?.payload ?? settingsResponse?.data ?? [];

    if (
      selectedTeamId.value &&
      !teams.value.some(
        team => Number(team.id) === Number(selectedTeamId.value)
      )
    ) {
      selectedTeamId.value = null;
      draft.value = null;
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Não foi possível carregar os departamentos.'
    );
  } finally {
    loading.value = false;
  }
};

const newDepartment = () => {
  selectedTeamId.value = null;

  draft.value = {
    is_new: true,
    team_id: null,

    name: '',
    description: '',
    allow_auto_assign: true,

    mail: emptyMail(null),
  };
};

const editDepartment = team => {
  selectedTeamId.value = team.id;

  const setting = settings.value.find(
    item => Number(item.team_id) === Number(team.id)
  );

  draft.value = {
    is_new: false,

    team_id: team.id,

    name: team.name || '',

    description: team.description || '',

    allow_auto_assign: team.allow_auto_assign !== false,

    mail: setting
      ? {
          ...setting,
          smtp_password: '',
        }
      : emptyMail(team),
  };
};

const buildMailPayload = (mail, teamId, teamName) => {
  const senderEmail = mail.sender_email?.trim();

  const inboundDomain = mail.inbound_domain?.trim();

  const inboundLocalPart = mail.inbound_local_part?.trim();

  const hasMailConfiguration = Boolean(
    senderEmail || inboundDomain || inboundLocalPart
  );

  if (!hasMailConfiguration) {
    return null;
  }

  const payload = {
    team_id: Number(teamId),

    transport_mode: mail.transport_mode,

    inbound_enabled: Boolean(mail.inbound_enabled),

    outbound_enabled: Boolean(mail.outbound_enabled),

    sender_name: mail.sender_name?.trim() || teamName || null,

    sender_email: senderEmail || null,

    inbound_domain: inboundDomain || null,

    inbound_local_part: inboundLocalPart || null,

    smtp_address: mail.smtp_address?.trim() || null,

    smtp_port: mail.smtp_port ? Number(mail.smtp_port) : null,

    smtp_username: mail.smtp_username?.trim() || null,

    smtp_domain: mail.smtp_domain?.trim() || null,

    smtp_authentication: mail.smtp_authentication?.trim() || null,

    smtp_enable_starttls_auto: Boolean(mail.smtp_enable_starttls_auto),

    smtp_ssl: Boolean(mail.smtp_ssl),
  };

  if (mail.smtp_password?.trim()) {
    payload.smtp_password = mail.smtp_password;
  }

  return payload;
};

const saveMail = async (team, mail) => {
  const existing = settings.value.find(
    item => Number(item.team_id) === Number(team.id)
  );

  const payload = buildMailPayload(mail, team.id, team.name);

  if (!payload) {
    if (existing) {
      await TicketsAPI.deleteDepartmentMailSetting(existing.id);
    }

    return;
  }

  if (existing) {
    await TicketsAPI.updateDepartmentMailSetting(existing.id, payload);

    return;
  }

  await TicketsAPI.createDepartmentMailSetting(payload);
};

const validateMail = () => {
  const mail = draft.value.mail;

  const hasInbound =
    mail.inbound_domain?.trim() || mail.inbound_local_part?.trim();

  if (
    hasInbound &&
    (!mail.inbound_domain?.trim() || !mail.inbound_local_part?.trim())
  ) {
    useAlert('Para ativar a entrada, informe o identificador e o domínio.');

    return false;
  }

  if (
    mail.outbound_enabled &&
    (mail.inbound_domain?.trim() || mail.inbound_local_part?.trim()) &&
    !mail.sender_email?.trim()
  ) {
    useAlert('Informe o e-mail do remetente para a saída.');

    return false;
  }

  if (
    mail.transport_mode === 'custom_smtp' &&
    mail.sender_email?.trim() &&
    (!mail.smtp_address?.trim() || !mail.smtp_port)
  ) {
    useAlert('Informe o servidor e a porta do SMTP próprio.');

    return false;
  }

  return true;
};

const saveDepartment = async () => {
  if (!draft.value?.name?.trim()) {
    useAlert('Informe o nome do departamento.');

    return;
  }

  if (!validateMail()) {
    return;
  }

  saving.value = true;

  try {
    const teamPayload = {
      name: draft.value.name.trim(),

      description: draft.value.description?.trim() || '',

      allow_auto_assign: Boolean(draft.value.allow_auto_assign),
    };

    let response;

    if (draft.value.is_new) {
      response = await TeamsAPI.create(teamPayload);
    } else {
      response = await TeamsAPI.update(draft.value.team_id, teamPayload);
    }

    const team = response?.data?.payload ??
      response?.data ?? {
        id: draft.value.team_id,

        ...teamPayload,
      };

    if (!team?.id) {
      throw new Error('O departamento foi salvo, mas a API não retornou o ID.');
    }

    await saveMail(team, draft.value.mail);

    await load();

    const refreshed = teams.value.find(
      item => Number(item.id) === Number(team.id)
    );

    if (refreshed) {
      editDepartment(refreshed);
    }

    useAlert('Departamento salvo com sucesso.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Não foi possível salvar o departamento.'
    );
  } finally {
    saving.value = false;
  }
};

const removeDepartment = async team => {
  if (!team) {
    return;
  }

  if (!window.confirm(`Excluir o departamento "${team.name}"?`)) {
    return;
  }

  saving.value = true;

  try {
    const setting = settings.value.find(
      item => Number(item.team_id) === Number(team.id)
    );

    if (setting) {
      await TicketsAPI.deleteDepartmentMailSetting(setting.id);
    }

    await TeamsAPI.delete(team.id);

    selectedTeamId.value = null;
    draft.value = null;

    await load();

    useAlert('Departamento excluído.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Não foi possível excluir o departamento.'
    );
  } finally {
    saving.value = false;
  }
};

const removeMailConfiguration = async () => {
  const setting = selectedSetting.value;

  if (!setting) {
    return;
  }

  if (!window.confirm('Remover a configuração de e-mail deste departamento?')) {
    return;
  }

  saving.value = true;

  try {
    await TicketsAPI.deleteDepartmentMailSetting(setting.id);

    await load();

    if (selectedTeam.value) {
      editDepartment(selectedTeam.value);
    }

    useAlert('Configuração de e-mail removida.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível remover a configuração.'
    );
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="max-w-6xl">
    <div class="flex items-start justify-between gap-5">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">Departamentos</h2>

        <p class="mt-1 max-w-3xl text-sm text-n-slate-10">
          Crie os departamentos da Central de Tickets e, dentro de cada um,
          configure entrada e saída.
        </p>
      </div>

      <button
        type="button"
        class="inline-flex h-10 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white"
        @click="newDepartment"
      >
        <span class="i-lucide-plus size-4" />

        Novo departamento
      </button>
    </div>

    <div v-if="loading" class="flex min-h-48 items-center justify-center">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="mt-6 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
      <aside
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="border-b border-n-weak px-4 py-3">
          <p class="text-sm font-semibold text-n-slate-12">
            Departamentos cadastrados
          </p>

          <p class="mt-1 text-xs text-n-slate-9">
            {{ teams.length }}
            departamento(s)
          </p>
        </div>

        <div v-if="!teams.length" class="p-5 text-sm text-n-slate-9">
          Nenhum departamento criado.
        </div>

        <button
          v-for="team in teams"
          :key="team.id"
          type="button"
          class="flex w-full items-center gap-3 border-b border-n-weak px-4 py-3 text-left last:border-b-0 hover:bg-n-alpha-2"
          :class="
            Number(selectedTeamId) === Number(team.id) ? 'bg-n-brand/10' : ''
          "
          @click="editDepartment(team)"
        >
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
          >
            <span class="i-lucide-building-2 size-4" />
          </span>

          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-medium text-n-slate-12">
              {{ team.name }}
            </span>

            <span class="mt-0.5 block truncate text-xs text-n-slate-9">
              {{
                settings.find(item => Number(item.team_id) === Number(team.id))
                  ?.sender_email || 'Sem e-mail configurado'
              }}
            </span>
          </span>
        </button>
      </aside>

      <main>
        <div
          v-if="!draft"
          class="flex min-h-72 items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-solid-1 p-8 text-center"
        >
          <div>
            <span
              class="i-lucide-building-2 mx-auto block size-8 text-n-slate-8"
            />

            <p class="mt-3 text-sm font-medium text-n-slate-12">
              Selecione um departamento
            </p>

            <p class="mt-1 text-xs text-n-slate-9">
              Ou crie um novo para configurar a Central.
            </p>
          </div>
        </div>

        <div v-else class="space-y-5">
          <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{ draft.is_new ? 'Novo departamento' : 'Departamento' }}
                </h3>

                <p class="mt-1 text-xs text-n-slate-9">
                  O departamento é a equipe responsável pelos tickets
                  encaminhados para ele.
                </p>
              </div>

              <button
                v-if="!draft.is_new"
                type="button"
                class="text-sm font-medium text-n-ruby-11"
                @click="removeDepartment(selectedTeam)"
              >
                Excluir
              </button>
            </div>

            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label>
                <span class="text-sm font-medium text-n-slate-12"> Nome </span>

                <input
                  v-model="draft.name"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  placeholder="Ex.: Financeiro"
                />
              </label>

              <label
                class="flex items-center gap-3 self-end rounded-lg border border-n-weak px-4 py-2.5"
              >
                <input
                  v-model="draft.allow_auto_assign"
                  type="checkbox"
                  class="size-4"
                />

                <span>
                  <span class="block text-sm font-medium text-n-slate-12">
                    Distribuição automática
                  </span>

                  <span class="block text-xs text-n-slate-9">
                    Permite atribuição automática para a equipe.
                  </span>
                </span>
              </label>

              <label class="md:col-span-2">
                <span class="text-sm font-medium text-n-slate-12">
                  Descrição
                </span>

                <textarea
                  v-model="draft.description"
                  rows="3"
                  class="mt-2 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
                  placeholder="Ex.: Financeiro, cobranças e pagamentos."
                />
              </label>
            </div>
          </section>

          <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <div class="flex items-center gap-3">
              <span
                class="flex size-9 items-center justify-center rounded-lg bg-n-alpha-2"
              >
                <span class="i-lucide-mail-plus size-4" />
              </span>

              <div>
                <h3 class="text-base font-semibold text-n-slate-12">Entrada</h3>

                <p class="text-xs text-n-slate-9">
                  Endereço que recebe novos tickets deste departamento.
                </p>
              </div>
            </div>

            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Identificador
                </span>

                <input
                  v-model="draft.mail.inbound_local_part"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  placeholder="financeiro"
                />

                <span class="mt-1 block text-xs text-n-slate-9">
                  Parte antes do @.
                </span>
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Domínio
                </span>

                <input
                  v-model="draft.mail.inbound_domain"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  placeholder="empresa.com.br"
                />

                <span class="mt-1 block text-xs text-n-slate-9">
                  Ex.: financeiro@empresa.com.br
                </span>
              </label>
            </div>

            <label
              class="mt-4 flex items-center gap-3 rounded-lg border border-n-weak px-4 py-3"
            >
              <input
                v-model="draft.mail.inbound_enabled"
                type="checkbox"
                class="size-4"
              />

              <span>
                <span class="block text-sm font-medium text-n-slate-12">
                  Entrada ativa
                </span>

                <span class="block text-xs text-n-slate-9">
                  Mensagens recebidas neste endereço podem criar ou atualizar
                  tickets.
                </span>
              </span>
            </label>
          </section>

          <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <div class="flex items-center gap-3">
              <span
                class="flex size-9 items-center justify-center rounded-lg bg-n-alpha-2"
              >
                <span class="i-lucide-send size-4" />
              </span>

              <div>
                <h3 class="text-base font-semibold text-n-slate-12">Saída</h3>

                <p class="text-xs text-n-slate-9">
                  Remetente usado quando o departamento responde.
                </p>
              </div>
            </div>

            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Nome do remetente
                </span>

                <input
                  v-model="draft.mail.sender_name"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  :placeholder="draft.name || 'Financeiro'"
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  E-mail do remetente
                </span>

                <input
                  v-model="draft.mail.sender_email"
                  type="email"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  placeholder="financeiro@empresa.com.br"
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Modo de envio
                </span>

                <select
                  v-model="draft.mail.transport_mode"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                >
                  <option value="alias">SMTP principal / alias</option>

                  <option value="custom_smtp">SMTP próprio</option>
                </select>
              </label>
            </div>

            <label
              class="mt-4 flex items-center gap-3 rounded-lg border border-n-weak px-4 py-3"
            >
              <input
                v-model="draft.mail.outbound_enabled"
                type="checkbox"
                class="size-4"
              />

              <span>
                <span class="block text-sm font-medium text-n-slate-12">
                  Saída ativa
                </span>

                <span class="block text-xs text-n-slate-9">
                  Permite enviar respostas utilizando este remetente.
                </span>
              </span>
            </label>

            <div
              v-if="draft.mail.transport_mode === 'custom_smtp'"
              class="mt-5 grid gap-4 border-t border-n-weak pt-5 md:grid-cols-2"
            >
              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Servidor SMTP
                </span>

                <input
                  v-model="draft.mail.smtp_address"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  placeholder="smtp.gmail.com"
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12"> Porta </span>

                <input
                  v-model.number="draft.mail.smtp_port"
                  type="number"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Usuário
                </span>

                <input
                  v-model="draft.mail.smtp_username"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12"> Senha </span>

                <input
                  v-model="draft.mail.smtp_password"
                  type="password"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                  :placeholder="
                    draft.mail.has_smtp_password
                      ? 'Senha já cadastrada'
                      : 'Senha SMTP'
                  "
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Domínio SMTP
                </span>

                <input
                  v-model="draft.mail.smtp_domain"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                />
              </label>

              <label>
                <span class="text-sm font-medium text-n-slate-12">
                  Autenticação
                </span>

                <select
                  v-model="draft.mail.smtp_authentication"
                  class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
                >
                  <option value="login">Login</option>

                  <option value="plain">Plain</option>

                  <option value="cram_md5">CRAM-MD5</option>
                </select>
              </label>

              <label
                class="flex items-center gap-3 rounded-lg border border-n-weak px-4 py-3"
              >
                <input
                  v-model="draft.mail.smtp_enable_starttls_auto"
                  type="checkbox"
                  class="size-4"
                />

                <span class="text-sm text-n-slate-12">
                  STARTTLS automático
                </span>
              </label>

              <label
                class="flex items-center gap-3 rounded-lg border border-n-weak px-4 py-3"
              >
                <input
                  v-model="draft.mail.smtp_ssl"
                  type="checkbox"
                  class="size-4"
                />

                <span class="text-sm text-n-slate-12"> SSL </span>
              </label>
            </div>

            <button
              v-if="!draft.is_new && selectedSetting"
              type="button"
              class="mt-5 text-sm font-medium text-n-ruby-11"
              @click="removeMailConfiguration"
            >
              Remover somente configuração de e-mail
            </button>
          </section>

          <div class="flex items-center justify-end gap-3">
            <button
              type="button"
              class="h-10 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-11"
              @click="draft = null"
            >
              Cancelar
            </button>

            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white disabled:opacity-50"
              :disabled="saving"
              @click="saveDepartment"
            >
              <Spinner v-if="saving" class="text-white" />

              <span v-else class="i-lucide-save size-4" />

              Salvar departamento
            </button>
          </div>
        </div>
      </main>
    </div>
  </section>
</template>
